"use client"

import { useEffect, useState, useRef } from "react"
import { getDatabase, ref, onValue } from "firebase/database"
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage"
import { Link } from "react-router-dom"
import Button from "./Button"
import "./LoanHistory.css"
import { useCurrentUid } from "../hooks/useCurrentUid"

export default function LoanHistory() {
  const userId = useCurrentUid()

  const [applications, setApplications] = useState([])
  const [selectedApp, setSelectedApp] = useState(null)
  const [documentUrls, setDocumentUrls] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [isListOpen, setIsListOpen] = useState(true)

  const storage = getStorage()
  const prevUserIdRef = useRef(null)
  const unsubscribeRef = useRef(null)

  // Main data listener — now handles async context perfectly
useEffect(() => {
  // 1. Early return if no user yet
  if (!userId) {
    setLoading(false);
    setError("No account selected.");
    setApplications([]);
    setSelectedApp(null);
    setDocumentUrls({});
    return;
  }

  // 2. Reset UI only when user actually changes
  setLoading(true);
  setError("");
  setApplications([]);
  setSelectedApp(null);
  setDocumentUrls({});

  const db = getDatabase();
  const applicationsRef = ref(db, `Finance/${userId}`);

  // 3. Listener
  const unsubscribe = onValue(
    applicationsRef,
    (snapshot) => {
      const data = snapshot.val();

      if (data) {
        const list = Object.entries(data)
          .map(([id, details]) => ({
            applicationId: id,
            ...details,
          }))
          .sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));

        setApplications(list);
      } else {
        setApplications([]);
      }

      // Only clear loading after first real snapshot
      setLoading(false);
    },
    (err) => {
      console.error("Database error:", err);
      setError("Failed to load loan applications.");
      setLoading(false);
    }
  );

  // 4. Cleanup only on unmount or userId change
  return () => {
    unsubscribe();
  };
}, [userId]); // ← important: only re-run when userId actually changes
  // Fetch document URLs
  useEffect(() => {
    if (!selectedApp || !userId) return

    const fetchUrls = async () => {
      const urls = {}
      const docTypes = ["aadhar", "pan", "salary", "bank", "employment"]

      for (const type of docTypes) {
        if (selectedApp.documents?.[type] !== "yes") {
          urls[type] = null
          continue
        }

        // Your Finance form saves as: aadhar_1735689012345.pdf
        const timestampPart = selectedApp.applicationId.slice(2) // LA1234567890 → 1234567890
        const patterns = [
          `Finance/${userId}/${type}_${timestampPart}`,           // Best match
          `Finance/${userId}/${selectedApp.applicationId}_${type}`,
          `Finance/${userId}/${type}_${Date.now()}`,
        ]

        let found = false
        for (const path of patterns) {
          try {
            const fileRef = storageRef(storage, path)
            const url = await getDownloadURL(fileRef)
            urls[type] = url
            found = true
            break
          } catch (_) { /* try next */ }
        }
        if (!found) urls[type] = null
      }

      setDocumentUrls(prev => ({
        ...prev,
        [selectedApp.applicationId]: urls
      }))
    }

    fetchUrls()
  }, [selectedApp, userId])

  const handleSelectApp = (app) => {
    setSelectedApp(app)
    if (window.innerWidth <= 768) {
      setIsListOpen(false)
    }
  }

  const handleViewDocument = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer")
  }

  // Loading
  if (loading) {
    return (
      <div className="lh-container">
        <div className="lh-spinner">
          <div className="lh-spinner-circle"></div>
        </div>
        <p>Loading applications...</p>
      </div>
    )
  }

  // Error or no account
  if (error || !userId) {
    return (
      <div className="lh-container">
        <p className="lh-error">{error || "No account selected."}</p>
        <Link to="/dashboard/finance">
          <Button className="lh-button-primary">Back to Finance</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="lh-page">
      <div className="lh-container">
        <Link to="/dashboard/finance">
          <Button className="lh-button-primary">Back to Finance</Button>
        </Link>
        <h2 className="lh-section-title">Loan Application History</h2>
        <p className="lh-section-subtitle">Select an application to view its details.</p>

        {applications.length === 0 ? (
          <p className="lh-no-applications">No loan applications found.</p>
        ) : (
          <div className="lh-content-wrapper">
            {/* Application List - EXACT SAME UI */}
            <div className={`lh-applications-list ${isListOpen ? 'lh-list-open' : ''}`}>
              <div className="lh-list-content">
                <h4>Applications</h4>
                {applications.map((app) => (
                  <div
                    key={app.applicationId}
                    className={`lh-application-item ${selectedApp?.applicationId === app.applicationId ? 'lh-selected' : ''}`}
                    onClick={() => handleSelectApp(app)}
                  >
                    <span className="lh-application-label">Application ID</span>
                    <h3 className="lh-application-id">{app.applicationId}</h3>
                    <p className="lh-timestamp">{app.timestamp}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lh-separator"></div>

            {/* Details Panel - 100% YOUR ORIGINAL UI */}
            <div className={`lh-details-panel ${selectedApp ? 'lh-details-visible' : ''}`}>
              {selectedApp ? (
                <>
                  <div className="lh-details-header">
                    <h3 className="lh-application-id">Application ID: {selectedApp.applicationId}</h3>
                    <p className="lh-timestamp">{selectedApp.status} | {selectedApp.timestamp}</p>
                  </div>

                  <div className="lh-details-content">
                    <div className="lh-details-left">
                      <div className="lh-card">
                        <h4>Personal Information</h4>
                        <p><i className="fas fa-user lh-icon"></i> <strong>Name:</strong> {selectedApp.personalInfo.firstName} {selectedApp.personalInfo.lastName}</p>
                        <p><i className="fas fa-envelope lh-icon"></i> <strong>Email:</strong> {selectedApp.personalInfo.email}</p>
                        <p><i className="fas fa-phone lh-icon"></i> <strong>Phone:</strong> {selectedApp.personalInfo.phone}</p>
                        <p><i className="fas fa-id-card lh-icon"></i> <strong>PAN:</strong> {selectedApp.personalInfo.pan || "Not provided"}</p>
                        <p><i className="fas fa-building lh-icon"></i> <strong>GSTIN:</strong> {selectedApp.personalInfo.gstin || "Not provided"}</p>
                        <p><i className="fas fa-birthday-cake lh-icon"></i> <strong>Date of Birth:</strong> {selectedApp.personalInfo.dateOfBirth || "Not provided"}</p>
                        <p><i className="fas fa-map-marker-alt lh-icon"></i> <strong>Address:</strong> {selectedApp.personalInfo.address || "Not provided"}</p>
                      </div>
                    </div>

                    <div className="lh-details-right">
                      <div className="lh-card">
                        <h4>Financial Details</h4>
                        <p><i className="fas fa-money-bill lh-icon"></i> <strong>Loan Type:</strong> {selectedApp.financialDetails.loanType}</p>
                        <p><i className="fas fa-rupee-sign lh-icon"></i> <strong>Loan Amount:</strong> ₹{selectedApp.financialDetails.loanAmount}</p>
                        <p><i className="fas fa-clock lh-icon"></i> <strong>Tenure:</strong> {selectedApp.financialDetails.tenure}</p>
                        <p><i className="fas fa-wallet lh-icon"></i> <strong>Monthly Income:</strong> ₹{selectedApp.financialDetails.monthlyIncome}</p>
                        <p><i className="fas fa-briefcase lh-icon"></i> <strong>Employment Type:</strong> {selectedApp.financialDetails.employmentType}</p>
                        <p><i className="fas fa-building lh-icon"></i> <strong>Company:</strong> {selectedApp.financialDetails.company}</p>
                      </div>

                      <div className="lh-card">
                        <h4>Documents</h4>
                        {/* AADHAR */}
                        <p>
                          <i className={`fas ${selectedApp.documents?.aadhar === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}></i>
                          <span>Aadhar</span>
                          {selectedApp.documents?.aadhar === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.aadhar)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.aadhar}
                                aria-label="View Aadhar document"
                              ></button>
                              {!documentUrls[selectedApp.applicationId]?.aadhar && <span className="lh-url-unavailable"></span>}
                            </>
                          )}
                        </p>

                        {/* PAN */}
                        <p>
                          <i className={`fas ${selectedApp.documents?.pan === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}></i>
                          <span>PAN</span>
                          {selectedApp.documents?.pan === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.pan)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.pan}
                              ></button>
                              {!documentUrls[selectedApp.applicationId]?.pan && <span className="lh-url-unavailable"></span>}
                            </>
                          )}
                        </p>

                        {/* SALARY */}
                        <p>
                          <i className={`fas ${selectedApp.documents?.salary === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}></i>
                          <span>Salary</span>
                          {selectedApp.documents?.salary === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.salary)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.salary}
                              ></button>
                              {!documentUrls[selectedApp.applicationId]?.salary && <span className="lh-url-unavailable"></span>}
                            </>
                          )}
                        </p>

                        {/* BANK */}
                        <p>
                          <i className={`fas ${selectedApp.documents?.bank === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}></i>
                          <span>Bank</span>
                          {selectedApp.documents?.bank === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.bank)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.bank}
                              ></button>
                              {!documentUrls[selectedApp.applicationId]?.bank && <span className="lh-url-unavailable"></span>}
                            </>
                          )}
                        </p>

                        {/* EMPLOYMENT */}
                        <p>
                          <i className={`fas ${selectedApp.documents?.employment === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}></i>
                          <span>Employment</span>
                          {selectedApp.documents?.employment === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.employment)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.employment}
                              ></button>
                              {!documentUrls[selectedApp.applicationId]?.employment && <span className="lh-url-unavailable"></span>}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="lh-no-selection">
                  <p>Please select an application to view its details.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}